import { keepPreviousData } from "@tanstack/react-query";
import { RouterOutputs, trpc } from "../trpc";
import { OrganizationZapFolderId } from "@zapier/bff";
import { DEFAULT_FOLDER_ID } from "@zapier/utils";

interface Input {
  enabled?: boolean;
  folderId?: OrganizationZapFolderId;
  limit?: number;
  offset?: number;
  ownerId?: number;
}

// The Organization service needs to be used to fetch Zaps for a specific folder.
// This query will return results that have been hydrated from the Storage API but
// may need further hydration for additional data like owner info.

export type OrganizationZaps = RouterOutputs["zap"]["organizationZaps"];

export function useQueryOrganizationZaps({
  enabled,
  folderId,
  limit,
  offset = 0,
  ownerId,
}: Input) {
  const { data, isLoading, isFetching, isError, error } =
    trpc.zap.organizationZaps.useQuery(
      {
        folderId: folderId || DEFAULT_FOLDER_ID,
        offset,
        // If the limit is 0, still make a call to get the total # of zaps
        limit: limit === 0 ? 25 : limit,
        ownerId,
      },
      {
        enabled: folderId !== undefined && enabled,
        placeholderData: keepPreviousData,
        trpc: {
          context: {
            // Skip batching to avoid bundling this critical procedure with slower requests
            skipBatching: true,
          },
        },
      },
    );

  return {
    errorStatus: error?.data?.httpStatus,
    isError,
    isFetching: isFetching,
    isLoading: isLoading,
    response: data,
    // If the limit is 0, we should hide the zaps.
    // This is useful when we want to
    // determine the total # of zaps since we still want a response
    hideZaps: limit === 0,
  };
}
